import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { ReactComponent as ArrowDown } from 'assets/images/icons/arrow-list.svg';

import useClickOutside from 'hooks/useClickOutside';

import styles from 'assets/scss/modules/Gateways/Gateways.module.scss';

export interface FilterSelectOption {
  value: string;
  label: string;
}

interface Props {
  /** Selected option value. Empty string selects the "all" option. */
  value: string;
  /** Label for the always-first reset option (e.g. "All users"). */
  allLabel: string;
  options: FilterSelectOption[];
  onChange: (value: string) => void;
}

/**
 * FR-83 / FR-84: searchable single-select for the gateways filters.
 * Clicking the field turns it into a search input; the list opens below it.
 */
export const FilterSelect: React.FC<Props> = ({ value, allLabel, options, onChange }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useClickOutside(wrapRef, () => {
    setOpen(false);
    setSearch('');
  });

  useEffect(() => {
    if (open) {
      searchRef.current?.focus();
    } else {
      setSearch('');
    }
  }, [open]);

  // Sorted alphabetically by label (FR-83/84 3a).
  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.label.localeCompare(b.label)),
    [options],
  );

  // Smart partial-match search (FR-83/84 2a/2c/2e).
  const filteredOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return sortedOptions;
    return sortedOptions.filter((o) => o.label.toLowerCase().includes(needle));
  }, [sortedOptions, search]);

  const selectedLabel = useMemo(() => {
    if (!value) return allLabel;
    return options.find((o) => o.value === value)?.label || allLabel;
  }, [options, value, allLabel]);

  const handlePick = (next: string) => {
    onChange(next);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={wrapRef} className={styles.searchSelect}>
      <div
        className={classNames(styles.searchSelectTrigger, {
          [styles.searchSelectTriggerOpen]: open,
        })}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? (
          <input
            ref={searchRef}
            type="text"
            className={styles.searchSelectInput}
            placeholder="Search"
            value={search}
            autoComplete="off"
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={classNames(styles.searchSelectValue, {
              [styles.searchSelectValueEmpty]: !value,
            })}
          >
            {selectedLabel}
          </span>
        )}
        <ArrowDown
          className={classNames(styles.searchSelectChevron, {
            [styles.searchSelectChevronOpen]: open,
          })}
        />
      </div>

      {open && (
        <div className={styles.searchSelectDropdown}>
          <div className={styles.searchSelectList} role="listbox">
            <button
              type="button"
              role="option"
              aria-selected={value === ''}
              className={classNames(styles.searchSelectOption, {
                [styles.searchSelectOptionActive]: value === '',
              })}
              onClick={() => handlePick('')}
            >
              {allLabel}
            </button>

            {filteredOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={value === o.value}
                className={classNames(styles.searchSelectOption, {
                  [styles.searchSelectOptionActive]: value === o.value,
                })}
                onClick={() => handlePick(o.value)}
              >
                {o.label}
              </button>
            ))}

            {search.trim() && filteredOptions.length === 0 && (
              <div className={styles.searchSelectEmpty}>No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
